/**
 * Договорная неустойка и проценты по ст. 395 ГК.
 * Формулы совпадают с App\Service\Legal\PenaltyCalculator: дни включительно,
 * отрезки ключевой ставки режутся по календарным годам, база — 365 или 366.
 * Мораторий 01.04.2022–01.10.2022 не вычитается, только предупреждение.
 */
import {
  addDaysIso,
  compareIso,
  daysInYearOf,
  daysInclusive,
  formatRuDate,
  parseIsoDate,
  roundHalfUp,
} from "@/lib/legal/calendar"
import {
  ART395_KEY_RATE_FROM,
  KEY_RATE_SOURCE,
  KEY_RATE_UPDATED_AT,
  keyRateSegments,
} from "@/lib/legal/key-rate-cbr"

export { courtFee } from "@/lib/legal/court-fee-arbitrazh"
export type { CourtFeeResult } from "@/lib/legal/court-fee-arbitrazh"

const MORATORIA: readonly { from: string; to: string; note: string }[] = [
  {
    from: "2022-04-01",
    to: "2022-10-01",
    note: "Период пересекается с мораторием на банкротство (постановление Правительства РФ от 28.03.2022 № 497). За 01.04.2022–01.10.2022 неустойка и проценты, как правило, не начисляются, если должник не отказался от моратория.",
  },
]

export type PenaltyFormula = "art395" | "contractual"

export type PenaltyPeriod = {
  from: string
  to: string
  days: number
  ratePercent: number
  daysInYear: number | null
  amount: number
}

export type PenaltyResult = {
  formula: PenaltyFormula
  label: string
  principal: number
  from: string
  to: string
  days: number
  total: number
  periods: PenaltyPeriod[]
  capped: boolean
  notes: string[]
  sourceName: string | null
  sourceUpdatedAt: string | null
}

function assertPrincipal(principal: number): void {
  if (!(principal > 0) || !Number.isFinite(principal)) {
    throw new Error("Сумма долга должна быть больше нуля")
  }
}

function assertPeriod(from: string, to: string): void {
  parseIsoDate(from)
  parseIsoDate(to)
  if (compareIso(from, to) > 0) {
    throw new Error("Дата начала просрочки позже даты окончания")
  }
}

function splitByYear(from: string, to: string): Array<[string, string]> {
  const parts: Array<[string, string]> = []
  let cursor = from
  while (compareIso(cursor, to) <= 0) {
    const yearEnd = `${cursor.slice(0, 4)}-12-31`
    const end = compareIso(yearEnd, to) < 0 ? yearEnd : to
    parts.push([cursor, end])
    cursor = addDaysIso(end, 1)
  }
  return parts
}

function moratoriumNotes(from: string, to: string): string[] {
  const notes: string[] = []
  for (const item of MORATORIA) {
    if (compareIso(from, item.to) <= 0 && compareIso(to, item.from) >= 0) {
      notes.push(item.note)
    }
  }
  return notes
}

function staleNotes(to: string): string[] {
  if (compareIso(to, KEY_RATE_UPDATED_AT) <= 0) return []
  return [
    `Ставки известны по ${formatRuDate(KEY_RATE_UPDATED_AT)}. Дальше расчёт идёт по последней известной ставке.`,
  ]
}

function totalOf(periods: PenaltyPeriod[], capped: boolean): number {
  if (capped) return periods[0]?.amount ?? 0
  const sum = periods.reduce((acc, period) => acc + period.amount, 0)
  return roundHalfUp(sum, 2)
}

function finish(
  formula: PenaltyFormula,
  label: string,
  principal: number,
  from: string,
  to: string,
  periods: PenaltyPeriod[],
  capped: boolean,
  notes: string[],
  sourceName: string | null,
  sourceUpdatedAt: string | null,
): PenaltyResult {
  return {
    formula,
    label,
    principal,
    from,
    to,
    days: daysInclusive(from, to),
    total: totalOf(periods, capped),
    periods,
    capped,
    notes,
    sourceName,
    sourceUpdatedAt,
  }
}

export function art395(principal: number, from: string, to: string): PenaltyResult {
  assertPrincipal(principal)
  assertPeriod(from, to)
  if (compareIso(from, ART395_KEY_RATE_FROM) < 0) {
    throw new Error(
      `Расчёт по ключевой ставке доступен с ${formatRuDate(ART395_KEY_RATE_FROM)}. Для более ранних периодов применялись другие ставки.`,
    )
  }

  const periods: PenaltyPeriod[] = []
  for (const segment of keyRateSegments(from, to)) {
    for (const [partFrom, partTo] of splitByYear(segment.from, segment.to)) {
      const days = daysInclusive(partFrom, partTo)
      const yearDays = daysInYearOf(partFrom)
      periods.push({
        from: partFrom,
        to: partTo,
        days,
        ratePercent: segment.percent,
        daysInYear: yearDays,
        amount: roundHalfUp((principal * segment.percent) / 100 * days / yearDays, 2),
      })
    }
  }

  return finish(
    "art395",
    "Проценты по ст. 395 ГК РФ",
    principal,
    from,
    to,
    periods,
    false,
    [...staleNotes(to), ...moratoriumNotes(from, to)],
    KEY_RATE_SOURCE,
    KEY_RATE_UPDATED_AT,
  )
}

export function contractual(
  principal: number,
  from: string,
  to: string,
  dailyRatePercent: number,
  capPercent: number | null = null,
): PenaltyResult {
  assertPrincipal(principal)
  assertPeriod(from, to)
  if (
    !Number.isFinite(dailyRatePercent) ||
    dailyRatePercent <= 0 ||
    dailyRatePercent > 100
  ) {
    throw new Error("Ставка неустойки должна быть больше 0 и не больше 100% в день")
  }
  if (
    capPercent !== null &&
    (!Number.isFinite(capPercent) || capPercent <= 0 || capPercent > 1000)
  ) {
    throw new Error("Предел неустойки должен быть больше 0%")
  }

  const days = daysInclusive(from, to)
  let amount = roundHalfUp((principal * dailyRatePercent) / 100 * days, 2)
  let capped = false
  if (capPercent !== null) {
    const cap = roundHalfUp((principal * capPercent) / 100, 2)
    if (amount > cap) {
      amount = cap
      capped = true
    }
  }

  return finish(
    "contractual",
    "Договорная неустойка",
    principal,
    from,
    to,
    [
      {
        from,
        to,
        days,
        ratePercent: dailyRatePercent,
        daysInYear: null,
        amount,
      },
    ],
    capped,
    moratoriumNotes(from, to),
    null,
    null,
  )
}
